import { Component, EventEmitter, Inject, Injector, Input, OnInit, Optional, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ButtonActionsTypeEnum } from '@trendbuild/trend-z-api';
import { AbstractComponent } from '../../../commons/abstract.component';

@Component({
    selector: 'app-button-zapi-component',
    templateUrl: './button-zapi.v2.component.html',
    // templateUrl: './button-zapi.component.html',
    styleUrls: ['./button-zapi.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ButtonZapiComponent extends AbstractComponent implements OnInit {
  @Input() buttonActionsArray: FormArray;
  @Input() maxButtons: number = 3;
  @Input() title: string = '';
  @Input() message: string = '';
  @Input() footer: string = '';
  @Input() showFullMessage: boolean = false;
  @Input() campaign: boolean = false;
  @Input() previewOnly: boolean = false;
  @Input() showConfirmButton: boolean = false;
  @Output() titleChange = new EventEmitter<string>();
  @Output() messageChange = new EventEmitter<string>();
  @Output() footerChange = new EventEmitter<string>();
  
  ButtonActionsTypeEnum = ButtonActionsTypeEnum;
  currentTime: string = '';

  constructor(
    injector: Injector,
    private fb: FormBuilder,
    @Optional() private dialogRef?: MatDialogRef<ButtonZapiComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data?: { title?: string; message?: string; footer?: string; showFullMessage?: boolean; previewOnly?: boolean; showConfirmButton?: boolean; buttonActionsArray?: any[]; maxButtons?: number; }
  ) {
    super(injector);
  }

  ngOnInit(): void {
    if (this.data) {
      if (this.data.title !== undefined) this.title = this.data.title;
      if (this.data.message !== undefined) this.message = this.data.message;
      if (this.data.footer !== undefined) this.footer = this.data.footer;
      if (this.data.showFullMessage !== undefined) this.showFullMessage = this.data.showFullMessage;
      if (this.data.previewOnly !== undefined) this.previewOnly = this.data.previewOnly;
      if (this.data.showConfirmButton !== undefined) this.showConfirmButton = this.data.showConfirmButton;

      if (this.data.buttonActionsArray) {
        if (!this.buttonActionsArray) {
          this.buttonActionsArray = this.fb.array([]);

          if (Array.isArray(this.data.buttonActionsArray)) {
            this.data.buttonActionsArray.forEach(buttonData => {
              const buttonGroup = this.fb.group({
                type: [buttonData.type || ButtonActionsTypeEnum.reply, Validators.required],
                label: [buttonData.label || '', Validators.required],
                phone: [buttonData.phone || ''],
                url: [buttonData.url || ''],
              });

              this.buttonActionsArray.push(buttonGroup);
            });
          }
        }

        if (this.data.maxButtons) {
          this.maxButtons = this.data.maxButtons;
        }
      }
    }

    // Inicializar o formGroup para os campos de title, message e footer
    this.formGroup = this.fb.group({
      title: [this.title],
      message: [this.message, Validators.required],
      footer: [this.footer]
    });

    // Sincronizar as mudanças do formGroup com os @Outputs
    this.formGroup.get('title').valueChanges.subscribe(value => {
      this.title = value;
      this.titleChange.emit(value);
    });

    this.formGroup.get('message').valueChanges.subscribe(value => {
      this.message = value;
      this.messageChange.emit(value);
    });

    this.formGroup.get('footer').valueChanges.subscribe(value => {
      this.footer = value;
      this.footerChange.emit(value);
    });

    if (!this.previewOnly && (!this.buttonActionsArray || this.buttonActionsArray.length === 0)) {
      this.addButtonAction();
    }

    this.updateCurrentTime();
  }

  updateCurrentTime(): void {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    this.currentTime = `${hours}:${minutes}`;
  }

  addButtonAction(): void {
    if (!this.buttonActionsArray || this.buttonActionsArray.length >= this.maxButtons) {
      return;
    }

    const buttonForm = this.fb.group({
      type: [ButtonActionsTypeEnum.reply, Validators.required],
      label: ['', Validators.required],
      phone: [''],
      url: [''],
    });

    buttonForm.get('type').valueChanges.subscribe(type => {
      if (type === ButtonActionsTypeEnum.call) {
        buttonForm.get('phone').setValidators([Validators.required]);
        buttonForm.get('url').clearValidators();
      } else if (type === ButtonActionsTypeEnum.url) {
        buttonForm.get('phone').clearValidators();
        buttonForm.get('url').setValidators([Validators.required]);
      } else {
        buttonForm.get('phone').clearValidators();
        buttonForm.get('url').clearValidators();
      }
      buttonForm.get('phone').updateValueAndValidity();
      buttonForm.get('url').updateValueAndValidity();
    });

    this.buttonActionsArray.push(buttonForm);
  }

  removeButtonAction(index: number): void {
    if (this.buttonActionsArray) {
      this.buttonActionsArray.removeAt(index);
    }
  }

  getButtonGroup(index: number): FormGroup {
    return this.buttonActionsArray?.at(index) as FormGroup;
  }

  // Métodos legados mantidos para compatibilidade, mas agora usamos o formGroup
  onTitleChange(event: any): void {
    this.formGroup.get('title').setValue(event.target.value);
    // O evento titleChange já será emitido pelo valueChanges do controle
  }

  onMessageChange(event: any): void {
    this.formGroup.get('message').setValue(event.target.value);
    // O evento messageChange já será emitido pelo valueChanges do controle
  }

  onFooterChange(event: any): void {
    this.formGroup.get('footer').setValue(event.target.value);
    // O evento footerChange já será emitido pelo valueChanges do controle
  }

  confirm(): void {
    if (this.dialogRef) {
      this.dialogRef.close(true);
    }
  }

  cancel(): void {
    if (this.dialogRef) {
      this.dialogRef.close(false);
    }
  }

  get buttonsForDisplay(): any[] {
    if (!this.buttonActionsArray || this.buttonActionsArray.length === 0) {
      return [];
    }

    return this.buttonActionsArray.controls.map((control, index) => {
      const formGroup = control as FormGroup;
      return {
        id: `btn_${index}`,
        type: formGroup.get('type')?.value || ButtonActionsTypeEnum.reply,
        label: formGroup.get('label')?.value || `Botão ${index + 1}`,
        phone: formGroup.get('phone')?.value || '',
        url: formGroup.get('url')?.value || ''
      };
    });
  }

  formatPhone(event: Event) {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, '');

    if (value.length > 2) {
      value = `(${value.substring(0, 2)}) ${value.substring(2, 7)}${value.length > 7 ? '-' + value.substring(7, 11) : ''}`;
    }

    input.value = value;
  }

}
